import { isEmpty, omit, pick } from "lodash";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DataGridFilterType } from "./types";
import { isFilterChanged, isFilterEmpty } from "./utils";
import useMediaQuery from "components/utils/useMediaQuery";

export const useFilter = ({
    filterString = "",
    onFilterChange,
    onSortChange,
    notClearableFilterKeys,
}: {
    filterString?: string;
    onFilterChange?: (filterString: string) => void;
    onSortChange?: (key: string, forceAsc?: boolean) => void;
    notClearableFilterKeys: string[] | undefined;
}) => {
    const [filter, setFilter] = useState<DataGridFilterType>(getFilterObject(filterString));
    const isMobile = useMediaQuery("(max-width: 768px)");
    const [isFilterOpen, setIsFilterOpen] = useState(!isMobile);
    const [isSortOpen, setIsSortOpen] = useState(!isMobile);
    const [filterSelected, setFilterSelected] = useState("");

    const toggleFilter = useCallback((state?: boolean) => {
        setIsSortOpen(false);
        setIsFilterOpen((prev) => state ?? !prev);
    }, []);

    const toggleSort = useCallback((state?: boolean) => {
        setIsFilterOpen(false);
        setIsSortOpen((prev) => state ?? !prev);
    }, []);

    const filterInputRefs = useRef<Record<string, HTMLElement | null>>({});

    useEffect(() => {
        setFilter(getFilterObject(filterString));
    }, [filterString]);

    return useMemo(() => {
        const onFilterSubmit = (filterValue?: DataGridFilterType) => {
            setFilter(filterValue ?? {});
            onFilterChange && onFilterChange(getFilterString(filterValue ?? filter));
            setIsFilterOpen(false);
        };

        const onSetFilter = (filterObj: DataGridFilterType) => {
            setFilter(filterObj);
        };

        const onFieldClean = (key: string) => {
            if ((notClearableFilterKeys ?? []).includes(key)) return;

            const filterObj = omit(filter, [key]);
            onSetFilter(filterObj);
            onFilterChange && onFilterChange(getFilterString(filterObj));
        };

        const onFilterClear = () => {
            const filterObj = pick(filter, notClearableFilterKeys ?? []);
            onSetFilter(filterObj);
            onFilterChange && onFilterChange(getFilterString(filterObj));
        };

        const onSortClear = () => {
            onSortChange && onSortChange("", true);
        };

        const onFilterCancel = () => {
            onSetFilter(getFilterObject(filterString));
        };

        const onFilterValueChange = (key: string, value: string, submitFilter: boolean = false) => {
            const filterValue: DataGridFilterType = {
                ...(filter ?? {}),
                [key]: value,
            };

            onSetFilter(filterValue);

            if (submitFilter) {
                onFilterSubmit(filterValue);
            }
        };

        const onFilterValueKeyDown = (key: string, event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            if (event.key === "Enter") {
                event.preventDefault();
                const value = (event.target as HTMLInputElement).value;

                const filterValue: DataGridFilterType = {
                    ...(filter ?? {}),
                    [key]: value,
                };

                onSetFilter(filterValue);
                onFilterSubmit(filterValue);
            }
        };

        const onFilterInputFocus = (key: string) => {
            setFilterSelected(key);

            // Defer to the next tick so the ref is assigned and the input is mounted before focusing.
            setTimeout(() => {
                const element = filterInputRefs.current[key];
                element?.focus();
            }, 0);
        };

        const setFilterInputRef = (key: string, ref: typeof filterInputRefs.current[string]) => {
            filterInputRefs.current[key] = ref;
        };

        const getFilterValue = (key: string) => {
            return filter[key] ?? "";
        };

        return {
            filter,
            isFilterEmpty: isFilterEmpty(filter, notClearableFilterKeys),
            isFilterChanged: isFilterChanged(filter, notClearableFilterKeys),
            getFilterValue,
            onFilterValueChange,
            onFilterValueKeyDown,
            onFilterSubmit,
            onFilterClear,
            onSortClear,
            onFilterCancel,
            onFieldClean,
            isFilterOpen,
            isSortOpen,
            toggleFilter,
            toggleSort,
            filterSelected,
            onFilterInputFocus,
            setFilterInputRef,
        };
    }, [
        filter,
        filterString,
        notClearableFilterKeys,
        onFilterChange,
        onSortChange,
        isFilterOpen,
        isSortOpen,
        filterSelected,
        toggleFilter,
        toggleSort,
    ]);
};

export const getFilterObject = (filterString: string): DataGridFilterType => {
    if (isEmpty(filterString)) {
        return {};
    }

    return filterString
        .split("|")
        .filter((p: string) => !isEmpty(p))
        .reduce((result, next) => {
            const [key, value] = next.split("=");

            return {
                ...result,
                [key]: value,
            };
        }, {});
};

export const getFilterString = (filterObj: DataGridFilterType) => {
    if (isEmpty(filterObj)) {
        return "";
    }

    return Object.entries(filterObj)
        .filter(([_, value]) => !isEmpty(value))
        .map(([key, value]) => `${key}=${value}`)
        .join("|");
};
